import React, { useState, useMemo } from "react";
import { collection, addDoc } from "firebase/firestore";
import { getDb } from "@repo/backend/firebase";
import { logConversionEvent } from "@repo/backend/firebase/analytics";
import { generateSlots } from "../lib/generateSlots.js";
import type { Resource, AvailabilityRule } from "../schema/booking.js";
import { useAvailability } from "../data/useAvailability.js";
import { useBookings } from "../data/useBookings.js";

// Fallback fixture used when Firestore data hasn't loaded yet (offline / first render)
const FALLBACK_RESOURCE: Resource = {
  id: "table-demo",
  clientId: "demo",
  type: "table",
  name: "Main Dining",
  capacityMin: 1,
  capacityMax: 8,
  serviceIds: [],
  active: true,
};
const FALLBACK_RULES: AvailabilityRule[] = [
  { id: "rule-demo", resourceId: "table-demo", days: [0, 2, 3, 4, 5, 6], startTime: "17:00", endTime: "22:00" },
];

// ── Pure helpers ──────────────────────────────────────────────────────────────

function parseDateStr(dateStr: string) {
  const [y, m, d] = dateStr.split("-").map(Number);
  const date = new Date(y!, m! - 1, d!);
  return {
    weekday: date.toLocaleDateString("en-US", { weekday: "short" }),
    dayNum: date.toLocaleDateString("en-US", { day: "numeric" }),
    full: date.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" }),
    dayOfWeek: date.getDay(),
  };
}

function formatTime(t: string): string {
  const [h, m] = t.split(":").map(Number);
  const hour = h! % 12 || 12;
  const ampm = h! < 12 ? "AM" : "PM";
  return `${hour}:${String(m!).padStart(2, "0")} ${ampm}`;
}

function addMinutes(time: string, minutes: number): string {
  const [h, m] = time.split(":").map(Number);
  const total = h! * 60 + m! + minutes;
  return `${String(Math.floor(total / 60) % 24).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}

function getUpcomingDates(count: number): string[] {
  const result: string[] = [];
  const today = new Date();
  for (let i = 0; i < count; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    result.push([
      d.getFullYear(),
      String(d.getMonth() + 1).padStart(2, "0"),
      String(d.getDate()).padStart(2, "0"),
    ].join("-"));
  }
  return result;
}

// ── Icons ─────────────────────────────────────────────────────────────────────

function ChevronLeft() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
      <path d="M9 2L4 7L9 12" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Spinner() {
  return (
    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

// ── Props ─────────────────────────────────────────────────────────────────────

export interface BookingGridProps {
  clientId?: string;
  headline?: string;
  subheadline?: string;
  maxPartySize?: number;
  granularityMinutes?: number;
  durationMinutes?: number;
  bookingMode?: "instant" | "request";
  /**
   * Resource type to look up from availability data.
   * "table" (default, food-bev), "crew" or "staff" (service vertical).
   */
  resourceType?: "table" | "staff" | "crew" | "room";
  /**
   * When set, a non-refundable deposit of this amount (cents) is shown
   * in the confirmation step before the booking is written.
   * The actual charge is handled server-side via Stripe Connect.
   */
  depositAmountCents?: number;
  depositLabel?: string; // e.g. "$20 non-refundable deposit applied to your total"
  /** Whether to show the party-size stepper (true for tables; false for crew/staff) */
  showPartySize?: boolean;
  /** Label for the "party size" selector (default: "Guests") */
  partySizeLabel?: string;
}

type Step = "datetime" | "time" | "contact" | "deposit" | "done";

const INPUT_CLS =
  "w-full rounded-xl border border-border bg-background px-3.5 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 transition-shadow";

// ── Component ─────────────────────────────────────────────────────────────────

export function BookingGrid({
  clientId = "",
  headline = "Make a Reservation",
  subheadline,
  maxPartySize = 8,
  granularityMinutes = 30,
  durationMinutes = 90,
  bookingMode = "request",
  resourceType = "table",
  depositAmountCents = 0,
  depositLabel,
  showPartySize,
  partySizeLabel = "Guests",
}: BookingGridProps) {
  // For service verticals (crew/staff) hide the party-size picker by default
  const showPartySizePicker = showPartySize ?? (resourceType === "table");
  const dates = useMemo(() => getUpcomingDates(28), []);

  const [step, setStep] = useState<Step>("datetime");
  const [selectedDate, setSelectedDate] = useState("");
  const [partySize, setPartySize] = useState(2);
  const [selectedSlot, setSelectedSlot] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // ── Real data ────────────────────────────────────────────────────────────────
  const availabilityQuery = useAvailability(clientId);
  const bookingsQuery = useBookings(clientId, selectedDate);

  // Pick the first active resource matching resourceType; fall back to demo fixture while loading
  const resource: Resource = useMemo(() => {
    const r = availabilityQuery.data?.resources.find((r) => r.type === resourceType && r.active);
    return r ?? FALLBACK_RESOURCE;
  }, [availabilityQuery.data, resourceType]);

  const rules: AvailabilityRule[] = availabilityQuery.data?.rules ?? FALLBACK_RULES;

  // Days that have at least one availability rule — all others are closed
  const availableDays = useMemo(
    () => new Set(rules.flatMap((r) => r.days)),
    [rules]
  );

  const slots = useMemo(() => {
    if (!selectedDate) return [];
    return generateSlots({
      resource,
      rules,
      exceptions: [],
      existingBookings: bookingsQuery.data ?? [],
      date: selectedDate,
      granularityMinutes,
      durationMinutes,
      partySize,
    });
  }, [selectedDate, partySize, granularityMinutes, durationMinutes, resource, rules, bookingsQuery.data]);

  const selectedDateInfo = selectedDate ? parseDateStr(selectedDate) : null;
  const isDateClosed = selectedDate ? !availableDays.has(parseDateStr(selectedDate).dayOfWeek) : false;
  const hasAvailableSlots = slots.some((s) => s.available);

  function handleDateSelect(date: string) {
    if (!availableDays.has(parseDateStr(date).dayOfWeek)) return;
    setSelectedDate(date);
    setSelectedSlot("");
  }

  function handleSlotSelect(startTime: string) {
    setSelectedSlot(startTime);
    setStep("contact");
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    // If a deposit is required, show deposit step before writing
    if (hasDeposit && step === "contact") {
      setStep("deposit");
      return;
    }

    setSubmitting(true);
    setSubmitError("");

    try {
      const now = new Date().toISOString();
      await addDoc(
        collection(getDb(), "clients", resource.clientId || clientId, "reservations"),
        {
          clientId: resource.clientId || clientId,
          resourceId: resource.id,
          customerName: name,
          customerEmail: email,
          customerPhone: phone || null,
          partySize: showPartySizePicker ? partySize : 1,
          serviceId: null,
          date: selectedDate,
          startTime: selectedSlot,
          endTime: addMinutes(selectedSlot, durationMinutes),
          durationMinutes,
          notes: notes || null,
          status: "pending",
          // Deposit: record the amount; actual charge is triggered server-side via Stripe Connect
          depositStatus: hasDeposit ? "required" : "none",
          depositAmountCents: hasDeposit ? depositAmountCents : 0,
          createdAt: now,
          updatedAt: now,
        }
      );
      logConversionEvent(resourceType === "table" ? "book_reservation" : "book_appointment", {
        resource_type: resourceType,
        party_size: showPartySizePicker ? partySize : 1,
      });
      setStep("done");
    } catch (err) {
      console.error("[BookingGrid] reservation write failed:", err);
      setSubmitError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  function handleReset() {
    setStep("datetime");
    setSelectedDate("");
    setSelectedSlot("");
    setName("");
    setEmail("");
    setPhone("");
    setNotes("");
    setSubmitError("");
  }

  function formatDepositDollars(cents: number) {
    return `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  }

  const hasDeposit = depositAmountCents > 0;
  const ctaLabel = bookingMode === "instant" ? "Reserve" : "Request";
  const totalSteps = hasDeposit ? 4 : 3;
  const stepNum = step === "datetime" ? 0 : step === "time" ? 1 : step === "contact" ? 2 : step === "deposit" ? 3 : totalSteps;

  return (
    <div className="max-w-xl mx-auto">
      {/* Section heading */}
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-foreground mb-2">{headline}</h2>
        {subheadline && <p className="text-muted-foreground text-base">{subheadline}</p>}
      </div>

      {/* Card */}
      <div className="bg-background rounded-2xl border border-border shadow-[0_8px_40px_-8px_rgba(0,0,0,0.12)] overflow-hidden">
        {/* Step indicator */}
        {step !== "done" && (
          <div className="flex items-center justify-center gap-2 pt-7 px-8">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={i}
                className={[
                  "h-1.5 rounded-full transition-all duration-300",
                  i === stepNum ? "w-7 bg-primary" : i < stepNum ? "w-2 bg-primary/35" : "w-2 bg-border",
                ].join(" ")}
              />
            ))}
          </div>
        )}

        {/* ── Step 1: Date & party size ── */}
        {step === "datetime" && (
          <div className="px-8 pt-6 pb-8">
            <p className="text-sm font-semibold text-foreground mb-4">Select a date &amp; party size</p>

            {/* Date strip */}
            <div className="overflow-x-auto -mx-1 px-1 mb-5">
              <div className="flex gap-2 pb-1" style={{ width: "max-content" }}>
                {dates.map((date) => {
                  const info = parseDateStr(date);
                  const closed = !availableDays.has(info.dayOfWeek);
                  const selected = date === selectedDate;
                  return (
                    <button
                      key={date}
                      type="button"
                      onClick={() => handleDateSelect(date)}
                      disabled={closed}
                      className={[
                        "flex flex-col items-center rounded-xl px-3 py-2.5 min-w-[50px] border transition-all",
                        closed
                          ? "opacity-30 cursor-not-allowed bg-muted/30 border-transparent"
                          : selected
                          ? "bg-primary text-primary-foreground border-primary shadow-sm"
                          : "bg-muted/40 border-transparent hover:bg-muted hover:border-border cursor-pointer",
                      ].join(" ")}
                    >
                      <span className="text-[9px] font-semibold uppercase tracking-wider opacity-70 mb-0.5">
                        {info.weekday}
                      </span>
                      <span className="text-sm font-bold">{info.dayNum}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {selectedDate && isDateClosed && (
              <p className="text-xs text-muted-foreground bg-muted/40 rounded-lg px-4 py-3 mb-5">
                We&apos;re closed that day — please choose another date.
              </p>
            )}

            {/* Party size stepper — only shown for table/room resource types */}
            {showPartySizePicker && (
              <div className="mb-7">
                <label className="block text-sm font-medium text-foreground mb-3">{partySizeLabel}</label>
                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    onClick={() => setPartySize((n) => Math.max(1, n - 1))}
                    disabled={partySize <= 1}
                    className="w-10 h-10 rounded-full border border-border flex items-center justify-center text-xl font-light hover:bg-muted transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    aria-label="Decrease"
                  >−</button>
                  <span className="text-base font-semibold text-foreground min-w-[90px] text-center tabular-nums">
                    {partySize} {partySize === 1 ? "guest" : "guests"}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPartySize((n) => Math.min(maxPartySize, n + 1))}
                    disabled={partySize >= maxPartySize}
                    className="w-10 h-10 rounded-full border border-border flex items-center justify-center text-xl font-light hover:bg-muted transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    aria-label="Increase"
                  >+</button>
                </div>
              </div>
            )}

            <button
              type="button"
              onClick={() => setStep("time")}
              disabled={!selectedDate || isDateClosed}
              className="w-full rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground hover:opacity-90 transition-opacity disabled:opacity-35 disabled:cursor-not-allowed"
            >
              {showPartySizePicker ? "See Available Times" : "See Available Slots"}
            </button>
          </div>
        )}

        {/* ── Step 2: Time slots ── */}
        {step === "time" && (
          <div className="px-8 pt-6 pb-8">
            <div className="flex items-center gap-2 mb-5 text-sm">
              <button
                type="button"
                onClick={() => setStep("datetime")}
                className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
              >
                <ChevronLeft />
                Back
              </button>
              <span className="text-muted-foreground/50">·</span>
              <span className="font-medium text-foreground truncate">
                {selectedDateInfo?.full}{showPartySizePicker ? ` · ${partySize} ${partySize === 1 ? "guest" : "guests"}` : ""}
              </span>
            </div>

            <p className="text-sm font-semibold text-foreground mb-4">Choose a time</p>

            {bookingsQuery.isLoading ? (
              <div className="flex items-center justify-center py-10 gap-2 text-sm text-muted-foreground">
                <Spinner />
                Checking availability…
              </div>
            ) : !hasAvailableSlots ? (
              <div className="text-center py-10 text-sm text-muted-foreground">
                <p>No availability for {partySize} guests on this date.</p>
                <button
                  type="button"
                  onClick={() => setStep("datetime")}
                  className="mt-3 text-primary underline underline-offset-4 hover:opacity-75 transition-opacity"
                >
                  Try a different date or party size
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {slots.map((slot) => (
                  <button
                    key={slot.startTime}
                    type="button"
                    onClick={() => slot.available && handleSlotSelect(slot.startTime)}
                    disabled={!slot.available}
                    className={[
                      "rounded-xl border px-3 py-3 text-sm font-medium transition-all",
                      slot.available
                        ? "border-border hover:border-primary/50 hover:bg-primary/5 text-foreground cursor-pointer"
                        : "border-border/30 bg-muted/20 text-muted-foreground/35 cursor-not-allowed",
                    ].join(" ")}
                  >
                    {formatTime(slot.startTime)}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Step 3: Contact details ── */}
        {step === "contact" && (
          <form onSubmit={handleSubmit} className="px-8 pt-6 pb-8">
            <div className="flex items-center gap-2 mb-5 text-sm">
              <button
                type="button"
                onClick={() => setStep("time")}
                className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
              >
                <ChevronLeft />
                Back
              </button>
              <span className="text-muted-foreground/50">·</span>
              <span className="font-medium text-foreground truncate">
                {selectedDateInfo?.full} · {formatTime(selectedSlot)} · {partySize}{" "}
                {partySize === 1 ? "guest" : "guests"}
              </span>
            </div>

            <p className="text-sm font-semibold text-foreground mb-4">Your details</p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Full name"
                  required
                  className={INPUT_CLS}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  className={INPUT_CLS}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  Phone <span className="font-normal text-muted-foreground">(optional)</span>
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="(555) 000-0000"
                  className={INPUT_CLS}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  Special requests <span className="font-normal text-muted-foreground">(optional)</span>
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Allergies, celebrations, accessibility needs…"
                  rows={3}
                  className={INPUT_CLS + " resize-none"}
                />
              </div>
            </div>

            {submitError && (
              <p className="mt-3 text-sm text-destructive">{submitError}</p>
            )}

            <button
              type="submit"
              disabled={!name || !email || submitting}
              className="mt-6 w-full rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground hover:opacity-90 transition-all disabled:opacity-35 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting ? (<><Spinner />Submitting…</>) : ctaLabel}
            </button>
          </form>
        )}

        {/* ── Step 4 (optional): Deposit confirmation ── */}
        {step === "deposit" && hasDeposit && (
          <div className="px-8 pt-6 pb-8">
            <button
              type="button"
              onClick={() => setStep("contact")}
              className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors mb-5"
            >
              <ChevronLeft />
              Back
            </button>

            <p className="text-sm font-semibold text-foreground mb-1">Confirm your reservation</p>
            <div className="text-sm text-muted-foreground mb-6 space-y-1">
              <p>{selectedDateInfo?.full} · {formatTime(selectedSlot)}</p>
              {showPartySizePicker && <p>{partySize} {partySize === 1 ? "guest" : "guests"}</p>}
            </div>

            {/* Deposit callout */}
            <div className="rounded-xl bg-primary/5 border border-primary/20 px-5 py-4 mb-6">
              <p className="text-sm font-semibold text-foreground mb-1">
                {formatDepositDollars(depositAmountCents)} non-refundable reservation fee
              </p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {depositLabel ?? `A ${formatDepositDollars(depositAmountCents)} fee is charged to hold your slot. It will be applied to the cost of your service.`}
              </p>
            </div>

            {submitError && (
              <p className="mb-3 text-sm text-destructive">{submitError}</p>
            )}

            <button
              type="button"
              onClick={handleSubmit as unknown as React.MouseEventHandler}
              disabled={submitting}
              className="w-full rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground hover:opacity-90 transition-all disabled:opacity-35 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting ? <><Spinner />Processing…</> : `Pay ${formatDepositDollars(depositAmountCents)} & Reserve`}
            </button>
            <p className="mt-3 text-xs text-muted-foreground text-center">
              Secure checkout via Stripe — we&apos;ll contact you to confirm the details.
            </p>
          </div>
        )}

        {/* ── Step 4/5: Confirmation ── */}
        {step === "done" && (
          <div className="px-8 py-14 text-center">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-5">
              <svg className="w-8 h-8 text-primary" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path d="M5 12L10 17L19 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-foreground mb-2">
              {bookingMode === "instant" ? "Slot Reserved!" : "Request Received!"}
            </h3>
            <div className="text-muted-foreground text-sm space-y-1 mb-2">
              <p className="font-medium text-foreground">{selectedDateInfo?.full}</p>
              <p>{formatTime(selectedSlot)}{showPartySizePicker ? ` · ${partySize} ${partySize === 1 ? "guest" : "guests"}` : ""}</p>
            </div>
            {hasDeposit && (
              <p className="text-xs text-muted-foreground mt-2">
                {formatDepositDollars(depositAmountCents)} deposit applied to your service total.
              </p>
            )}
            <p className="text-muted-foreground text-sm mt-4 mb-8 leading-relaxed max-w-xs mx-auto">
              {bookingMode === "request"
                ? "We'll confirm your booking shortly."
                : "Your slot is confirmed."}{" "}
              A confirmation has been sent to{" "}
              <span className="text-foreground font-medium">{email}</span>.
            </p>
            <button
              type="button"
              onClick={handleReset}
              className="text-sm text-primary underline underline-offset-4 hover:opacity-70 transition-opacity"
            >
              Make another reservation
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
